import { type FC } from "react";

import { AccountCircleIcon } from "@moment/icons";

import { Button } from "../../Button";
import { useCellServices } from "../../CellServicesContext";
import { Chip, stringToColorVariant } from "../../Chip";
import { DropdownMenu } from "../../DropdownMenu";
import { AvatarSize } from "../../MomentAvatar";
import { ReactionAlpha } from "../../ReactionAlpha";
import {
	CLEAR_SELECT_ITEM_VALUE,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "../../Select";
import { UserAvatarAlpha } from "../../UserAvatarAlpha";
import { type HydratedCell } from "../HydratedCell";

export const DialogTriggerCell: FC<{
	className?: string;
	cell: HydratedCell;
	disabled?: boolean;
}> = ({ className, cell, disabled: _disabled }) => {
	const { currentUser, users } = useCellServices();

	const disabled = cell.disabled || _disabled;

	switch (cell.type) {
		case "Text":
			return (
				<div className={className}>
					{cell.value.slice(0, 180)}
					{cell.value.length >= 180 && "…"}
				</div>
			);
		case "Reaction":
			return (
				<div className="w-fit" onClick={(e) => e.stopPropagation()}>
					<ReactionAlpha
						size="sm"
						currentUser={currentUser}
						reactionsValue={cell.value.map(({ user }) => user)}
						onChangeReactions={() => currentUser && cell.toggleReaction(currentUser)}
						disabled={disabled}
					/>
				</div>
			);
		case "Select": {
			const cellIsEmpty =
				cell.value === CLEAR_SELECT_ITEM_VALUE || cell.value === null || cell.value === "";
			return (
				<Select value={cell.value ?? CLEAR_SELECT_ITEM_VALUE} onValueChange={cell.update}>
					<SelectTrigger className="!border-none pl-2 -mr-1 w-fit" disabled={disabled}>
						{cellIsEmpty ? (
							<span className="text-secondary">Empty</span>
						) : (
							<div className="-mx-1">
								<Chip
									level="secondary"
									size="md"
									fontVariant="no-caps"
									colorVariant={
										cell.typeInfo?.options.find(
											({ label }) => label === cell.value
										)?.color ?? stringToColorVariant(cell.value)
									}
								>
									{cell.value}
								</Chip>
							</div>
						)}
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={CLEAR_SELECT_ITEM_VALUE}>Blank</SelectItem>
						{cell.typeInfo?.options.map(
							({ label, color }) =>
								label && (
									<SelectItem key={label} value={label ?? crypto.randomUUID()}>
										<Chip
											level="secondary"
											size="md"
											fontVariant="no-caps"
											colorVariant={color}
										>
											{label}
										</Chip>
									</SelectItem>
								)
						)}
					</SelectContent>
				</Select>
			);
		}
		case "Person": {
			const update = (userId: string | null) => {
				const user = userId ? (users.find((u) => u.id === userId) ?? null) : null;
				cell.update(user);
			};
			return (
				<DropdownMenu.Root>
					<DropdownMenu.Trigger asChild disabled={disabled}>
						<Button
							size="sm"
							preset="quaternary"
							elevation="flat"
							disabled={disabled}
							className="w-fit"
							onClick={(e) => e.stopPropagation()}
						>
							{!cell.value ? (
								<>
									<AccountCircleIcon />
									<span className="!text-tertiary">Empty</span>
								</>
							) : (
								<>
									<UserAvatarAlpha
										size={AvatarSize.xxs}
										imageURL={cell.value?.profilePicture}
										label={cell.value?.name}
									/>
								</>
							)}
						</Button>
					</DropdownMenu.Trigger>
					<DropdownMenu.Content>
						<DropdownMenu.Item className="text-secondary" onClick={() => update(null)}>
							<AccountCircleIcon className="m-0.5" /> Blank
						</DropdownMenu.Item>
						{users.map((user) => (
							<DropdownMenu.Item key={user.id} onClick={() => update(user.id)}>
								<UserAvatarAlpha
									size={AvatarSize.xs}
									imageURL={user.profilePicture}
									label={user.name}
								/>
							</DropdownMenu.Item>
						))}
					</DropdownMenu.Content>
				</DropdownMenu.Root>
			);
		}
		default: {
			const _exhaustive: never = cell;
			throw new Error(`Unknown cell type ${(_exhaustive as HydratedCell)?.type}`);
		}
	}
};
